/**
 * Centralised registry of `--hub-*` CSS custom-property names emitted by
 * `<HubThemingProvider>`'s wrapper `<div>`. These are the **only** v2 CSS-var
 * names extension code references — never hand-write `--hub-*` strings at the
 * call site.
 *
 * Why centralised:
 *   • Single rename point — change a name here, every consumer follows.
 *   • Type-safe lookup via `PaletteRef` — typo "primary.150" fails the build.
 *   • `resolveAdfColor` derives its `var(--hub-color-...)` output from this
 *     registry, so the renderer never sees raw var-name strings.
 *
 * Naming convention:
 *   --hub-color-{family}-{shade}    → e.g. --hub-color-primary-200
 *   --hub-color-{semantic}          → e.g. --hub-color-text-on-primary
 *   --hub-radius-{slot}             → reserved for future radius work
 *   --hub-space-{slot}              → reserved for future density work
 *
 * The wrapper div emits the registry as inline `style` so the values are
 * present in the SSR HTML on first paint — no useEffect, no setProperty.
 */
import type { PaletteFamily, PaletteRef, PaletteShade } from './types';

const SHADES: ReadonlyArray<PaletteShade> = [
	'100',
	'200',
	'300',
	'400',
	'500',
	'600',
	'700',
	'800',
	'900',
	'1000',
];

const FAMILIES: ReadonlyArray<PaletteFamily> = ['primary', 'accent'];

const buildColorVarMap = (): Record<PaletteRef, string> => {
	const out = {} as Record<PaletteRef, string>;
	for (const family of FAMILIES) {
		for (const shade of SHADES) {
			const ref: PaletteRef = `${family}.${shade}`;
			out[ref] = `--hub-color-${family}-${shade}`;
		}
	}
	return out;
};

/**
 * Map of `PaletteRef` ("primary.200") → CSS custom-property name
 * ("--hub-color-primary-200"). Frozen at module load so consumers can rely
 * on referential stability.
 */
export const HUB_COLOR_CSS_VARS: Readonly<Record<PaletteRef, string>> = Object.freeze(
	buildColorVarMap(),
);

/**
 * Public namespace re-export grouping all v2 CSS-var registries by category.
 * Future categories slot in here without churning the import surface:
 *   HUB_CSS_VARS.color    → palette refs → --hub-color-*
 *   HUB_CSS_VARS.radius   → radius slots → --hub-radius-*    (future)
 *   HUB_CSS_VARS.space    → density slots → --hub-space-*    (future)
 */
export const HUB_CSS_VARS: Readonly<{ color: typeof HUB_COLOR_CSS_VARS }> = Object.freeze({
	color: HUB_COLOR_CSS_VARS,
});
